// frontend/src/TimingEditor.tsx
// Editor fuer Timing-Definitionen (zwischen Dimension und Relation).

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useRef, useState } from "react";
import { SyntaxchecksData, Timing, TimingAnchor } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (value: string) =>
  SPECIAL_CHARACTERS.reduce((next, char) => next.split(char).join(""), value);

const ANCHOR_OPTIONS: TimingAnchor[] = [
  "today_start",
  "tomorrow_start",
  "yesterday_start",
  "current_week_start",
  "current_month_start",
  "planning_horizon_start",
  "planning_horizon_end",
];

type Props = {
  timings: Timing[];
  onChange: (timings: Timing[]) => void;
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  syntaxData?: SyntaxchecksData | null;
};

function fieldStyle(mode: Mode, width?: number | string): React.CSSProperties {
  return {
    width,
    maxWidth: "100%",
    minWidth: 0,
    padding: "6px 10px",
    borderRadius: 6,
    border:
      mode === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.1)",
    background:
      mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)",
    color: "inherit",
    boxSizing: "border-box",
  };
}

function getTimingError(timing: Timing, allNames: string[]): string | null {
  if (!timing.name.trim()) return "Name required";
  if (allNames.filter((name) => name === timing.name).length > 1) {
    return "Name already in use";
  }

  if (timing.type === "relative") {
    if (!timing.anchor) return "Anchor required";
    if (!timing.direction) return "Direction required";
    if (!timing.unit) return "Unit required";
    const length = Number(timing.length);
    if (!timing.length || !Number.isFinite(length) || length <= 0) {
      return "Length must be greater than 0";
    }
    return null;
  }

  if (!timing.start_date && !timing.end_date) {
    return "start_date or end_date required";
  }
  return null;
}

function sanitizeNumberText(value: string): string {
  return value.replace(/[^0-9]/g, "");
}

function nextForType(timing: Timing, type: Timing["type"]): Partial<Timing> {
  if (type === "relative") {
    return {
      type,
      anchor: timing.anchor ?? "current_week_start",
      direction: timing.direction ?? "future",
      length: timing.length,
      unit: "day",
      offset: timing.offset,
      start_date: undefined,
      end_date: undefined,
    };
  }

  return {
    type,
    anchor: undefined,
    direction: undefined,
    length: undefined,
    unit: undefined,
    offset: undefined,
    start_date: timing.start_date,
    end_date: timing.end_date,
  };
}

function SortableTimingCard({
  timing,
  expandedId,
  setExpandedId,
  onUpdate,
  allNames,
  userHue,
  mode,
}: {
  timing: Timing;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Timing>) => void;
  allNames: string[];
  userHue: number;
  mode: Mode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: timing.id });
  const expanded = expandedId === timing.id;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(
    null
  );
  const errorMessage = getTimingError(timing, allNames);
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  const cardStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: getBaseColor(4, "connection", userHue, mode),
    margin: "8px 0",
    padding: 8,
    borderRadius: 6,
    color: "inherit",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "0.95em",
    fontWeight: 500,
    minWidth: 86,
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  };

  const handleNameChange = (raw: string) => {
    const stripped = stripSpecialChars(raw);
    if (stripped !== raw) {
      const found = SPECIAL_CHARACTERS.filter((char) => raw.includes(char))
        .map((char) => (char === "\n" ? "\\n" : char))
        .join(", ");
      setSpecialCharWarning(found);
      window.setTimeout(() => setSpecialCharWarning(null), 2000);
    }
    onUpdate(timing.id, { name: stripped });
  };

  return (
    <div ref={setNodeRef} style={cardStyle} {...attributes}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div
          {...listeners}
          style={{ cursor: "grab" }}
          title="Drag to reorder · Drag out to remove"
        >
          ☰
        </div>
        <span
          style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }}
          onClick={() => setExpandedId(expanded ? null : timing.id)}
        >
          {expanded ? "▾" : "▸"}
        </span>

        {isEditingName ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              type="text"
              value={timing.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setIsEditingName(false);
              }}
              autoFocus
              className={`dimension-card__input ${
                errorMessage === "Name already in use"
                  ? "dimension-card__input--duplicate"
                  : ""
              }`}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.8em" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {timing.name}
          </div>
        )}

        {/* Syntax Validation Warning */}
        {errorMessage && (
          <span title={errorMessage} style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}>
            {validationIcon}
          </span>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 12, width: "100%", boxSizing: "border-box" }}>
          <div style={rowStyle}>
            <label style={labelStyle}>Type:</label>
            <select
              value={timing.type}
              onChange={(e) =>
                onUpdate(
                  timing.id,
                  nextForType(timing, e.target.value as Timing["type"])
                )
              }
              style={fieldStyle(mode, 150)}
            >
              <option value="relative">relative</option>
              <option value="absolute">absolute</option>
            </select>
          </div>

          {timing.type === "relative" ? (
            <>
              <div style={rowStyle}>
                <label style={labelStyle}>Anchor:</label>
                <select
                  value={timing.anchor ?? "current_week_start"}
                  onChange={(e) =>
                    onUpdate(timing.id, {
                      anchor: e.target.value as TimingAnchor,
                    })
                  }
                  style={fieldStyle(mode, "min(100%, 250px)")}
                >
                  {ANCHOR_OPTIONS.map((anchor) => (
                    <option key={anchor} value={anchor}>
                      {anchor}
                    </option>
                  ))}
                </select>
              </div>

              <div style={rowStyle}>
                <label style={labelStyle}>Direction:</label>
                <select
                  value={timing.direction ?? "future"}
                  onChange={(e) =>
                    onUpdate(timing.id, {
                      direction: e.target.value as Timing["direction"],
                    })
                  }
                  style={fieldStyle(mode, 150)}
                >
                  <option value="future">future</option>
                  <option value="past">past</option>
                </select>
              </div>

              <div style={rowStyle}>
                <label style={labelStyle}>Length:</label>
                <input
                  type="number"
                  min={1}
                  value={timing.length ?? ""}
                  onChange={(e) =>
                    onUpdate(timing.id, {
                      length: sanitizeNumberText(e.target.value) || undefined,
                    })
                  }
                  style={fieldStyle(mode, 90)}
                />
                <select
                  value={timing.unit ?? "day"}
                  onChange={(e) =>
                    onUpdate(timing.id, { unit: e.target.value as Timing["unit"] })
                  }
                  style={fieldStyle(mode, 100)}
                >
                  <option value="day">day</option>
                </select>
              </div>

              <div style={rowStyle}>
                <label style={labelStyle}>Offset:</label>
                <input
                  type="number"
                  min={0}
                  value={timing.offset ?? ""}
                  onChange={(e) => {
                    const raw = sanitizeNumberText(e.target.value);
                    onUpdate(timing.id, {
                      offset: raw === "" ? undefined : parseInt(raw, 10),
                    });
                  }}
                  style={fieldStyle(mode, 120)}
                />
              </div>
            </>
          ) : (
            <>
              <div style={rowStyle}>
                <label style={labelStyle}>start_date:</label>
                <input
                  type="date"
                  value={timing.start_date ?? ""}
                  onChange={(e) =>
                    onUpdate(timing.id, {
                      start_date: e.target.value || undefined,
                    })
                  }
                  style={fieldStyle(mode, 170)}
                />
              </div>

              <div style={rowStyle}>
                <label style={labelStyle}>end_date:</label>
                <input
                  type="date"
                  value={timing.end_date ?? ""}
                  onChange={(e) =>
                    onUpdate(timing.id, {
                      end_date: e.target.value || undefined,
                    })
                  }
                  style={fieldStyle(mode, 170)}
                />
              </div>
            </>
          )}

          {errorMessage && (
            <div className="syntax-error-banner">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TimingEditor({
  timings,
  onChange,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTimingId, setActiveTimingId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleAdd = () => {
    const newTiming: Timing = {
      id: Date.now().toString(),
      name: "Timing" + (timings.length + 1),
      type: "relative",
      anchor: "current_week_start",
      direction: "future",
      length: undefined,
      unit: "day",
    };
    onChange([...timings, newTiming]);
    setExpandedId(newTiming.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Timing>) => {
    onChange(timings.map((timing) => (timing.id === id ? { ...timing, ...updates } : timing)));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(timings.filter((timing) => timing.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  return (
    <div
      className="timing-editor"
      ref={editorFrameRef}
      style={{
        marginTop: "1em",
        marginBottom: "1em",
        padding: "0.5em",
        borderRadius: 8,
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      <div
        className="timing-editor__header"
        onClick={toggle}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong className="timing-editor__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isActive ? "▾" : "▸"} Timings
        </strong>
      </div>

      {isActive && (
        <div className="timing-editor__body" style={{ marginTop: "0.5em" }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event: DragStartEvent) => {
              setExpandedId(null);
              setActiveTimingId(String(event.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveTimingId(null);

              const finalRect =
                active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect &&
                editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (!over || active.id === over.id) return;
              const oldIndex = timings.findIndex((timing) => timing.id === active.id);
              const newIndex = timings.findIndex((timing) => timing.id === over.id);
              if (oldIndex === -1 || newIndex === -1) return;
              onChange(arrayMove(timings, oldIndex, newIndex));
            }}
          >
            <SortableContext
              items={timings.map((timing) => timing.id)}
              strategy={verticalListSortingStrategy}
            >
              {timings.map((timing) => (
                <SortableTimingCard
                  key={timing.id}
                  timing={timing}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  allNames={allNames}
                  userHue={userHue}
                  mode={mode}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeTimingId ? (
                <div
                  className="drag-overlay-card"
                  style={{
                    background: getBaseColor(4, "connection", userHue, mode),
                  }}
                >
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>
                    {timings.find((timing) => timing.id === activeTimingId)?.name ?? ""}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {timings.length === 0 && (
            <div
              style={{
                color:
                  mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                fontStyle: "italic",
                fontSize: "0.9em",
                padding: "8px 4px",
              }}
            >
              No timings defined.
            </div>
          )}

          <button onClick={handleAdd} className="capacity-editor__add">
            + Timing
          </button>
        </div>
      )}
    </div>
  );
}
